import React, { useState } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { MapPin, Package, AlertCircle, Edit3, X, Image } from 'lucide-react';
import { PickingLine } from './types';

interface ActiveLineProps {
  activeLine: PickingLine | null;
  onScanRequired: () => void;
  onLocationRequired: () => void;
  editingLocation?: number | null;
  editingQuantity?: number | null;
  newLocation?: string;
  newQuantity?: string;
  onStartLocationEdit?: (lineNo: number, location: string) => void;
  onStartQuantityEdit?: (lineNo: number, quantity: string) => void;
  onLocationChange?: (value: string) => void;
  onQuantityChange?: (value: string) => void;
  onLocationEdit?: (lineNo: number, location: string) => void;
  onQuantityEdit?: (lineNo: number, quantity: number) => void;
  onShowImage?: () => void;
}

export const ActiveLine: React.FC<ActiveLineProps> = ({
  activeLine,
  onScanRequired,
  onLocationRequired,
  editingLocation,
  editingQuantity,
  newLocation = '',
  newQuantity = '',
  onStartLocationEdit,
  onStartQuantityEdit,
  onLocationChange,
  onQuantityChange,
  onLocationEdit,
  onQuantityEdit,
  onShowImage
}) => {
  const { t } = useTranslation();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showFullItemNo, setShowFullItemNo] = useState(false);

  const handleKeyPress = (e: React.KeyboardEvent, type: 'location' | 'quantity') => {
    if (e.key === 'Enter' && activeLine) {
      if (type === 'location' && onLocationEdit) {
        onLocationEdit(activeLine.pListLineNo, newLocation);
      } else if (type === 'quantity' && onQuantityEdit) {
        onQuantityEdit(activeLine.pListLineNo, parseInt(newQuantity) || 0);
      }
    }
  };

  if (!activeLine) {
    return (
      <div className="bg-gray-50 rounded-lg shadow-sm p-2 sm:p-4 mb-2 sm:mb-4">
        <div className="text-center text-gray-500">
          <Package size={20} className="mx-auto mb-1 text-gray-300 sm:w-6 sm:h-6 sm:mb-2" />
          <p className="text-xs sm:text-sm font-medium">{t('warehouse.modules.picking.labels.noActiveLineShort')}</p>
        </div>
      </div>
    );
  }

  const isDescriptionTruncated = activeLine.itemDescription && activeLine.itemDescription.length > 40;
  const isItemNoTruncated = activeLine.itemNo && activeLine.itemNo.length > 20;

  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg shadow-sm p-2 sm:p-5 mb-2 sm:mb-4">
      {/* MOBILE: Stacked layout with tap-to-expand */}
      <div className="sm:hidden">
        {/* Row 1: Item number (full line, tap to expand) */}
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse flex-shrink-0"></div>
          <button
            onClick={() => setShowFullItemNo(!showFullItemNo)}
            className="font-bold text-sm text-gray-900 text-left flex-1 min-w-0 hover:text-blue-700 transition-colors"
          >
            {showFullItemNo ? (
              <span>{activeLine.itemNo}</span>
            ) : (
              <span className="truncate block">{activeLine.itemNo}</span>
            )}
          </button>
          {activeLine.image && onShowImage && (
            <button
              onClick={onShowImage}
              className="p-1.5 text-blue-600 hover:bg-blue-100 rounded min-w-[36px] min-h-[36px] flex items-center justify-center flex-shrink-0"
            >
              <Image size={16} />
            </button>
          )}
        </div>

        {/* Row 2: Description (full line, tap to expand) */}
        <div className="mb-1 pl-4">
          <button
            onClick={() => setShowFullDescription(!showFullDescription)}
            className="text-xs text-gray-600 text-left w-full hover:text-blue-700 transition-colors"
          >
            {showFullDescription ? (
              <span>{activeLine.itemDescription}</span>
            ) : (
              <span className="truncate block">{activeLine.itemDescription}</span>
            )}
          </button>
        </div>

        {/* Row 3: Location + Quantity */}
        <div className="flex items-center gap-2 text-sm pl-4 mb-1">
          <span className="font-medium text-gray-700">{activeLine.location}</span>
          <span className="text-gray-400">•</span>
          <span className="font-bold text-blue-700">{activeLine.qtyPicked}/{activeLine.qty}</span>
          {activeLine.orderNoERP && (
            <>
              <span className="text-gray-400">•</span>
              <span className="text-xs text-gray-500">#{activeLine.orderNoERP}</span>
            </>
          )}
        </div>

        {/* Row 4: Extra info fields from OrderLine (configured via UserProfile) */}
        {(activeLine.extraInfoField2Value || activeLine.extraInfoField3Value) && (
          <div className="flex items-center gap-2 text-xs font-medium pl-4">
            {activeLine.extraInfoField2Value && (
              <span className="text-red-600">{activeLine.extraInfoField2Value}</span>
            )}
            {activeLine.extraInfoField3Value && (
              <span className="text-green-600">{activeLine.extraInfoField3Value}</span>
            )}
          </div>
        )}
      </div>

      {/* DESKTOP: Full layout */}
      <div className="hidden sm:block">
        {/* Header with Order Info */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse"></div>
              <h3 className="text-base font-semibold text-blue-900">{t('warehouse.modules.picking.sections.activePickingLine')}</h3>
            </div>
            <div className="flex items-center gap-4 text-sm">
              {activeLine.orderNoERP && (
                <div className="text-gray-700 font-medium">{activeLine.orderNoERP}</div>
              )}
              {activeLine.customerName && (
                <div className="text-gray-600">{activeLine.customerName}</div>
              )}
            </div>
          </div>
          <div className="text-sm font-medium text-blue-700">#{activeLine.pListLineNo}</div>
        </div>

        {/* Content with larger elements */}
        <div className="bg-white rounded-md p-5 border border-blue-200">
          <div className="grid grid-cols-3 gap-6">
            {/* Item Info */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Package size={18} className="text-blue-600" />
                <span className="text-sm font-medium text-gray-600">{t('warehouse.modules.picking.labels.item')}</span>
                {activeLine.image && onShowImage && (
                  <button
                    onClick={onShowImage}
                    className="ml-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-md transition-colors border border-blue-300 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title={t('warehouse.modules.picking.labels.showHideImage')}
                  >
                    <Image size={20} strokeWidth={2.5} />
                  </button>
                )}
              </div>
              <div className="text-xl font-bold text-gray-900 mb-1">{activeLine.itemNo}</div>
              <div className="relative">
                {!showFullDescription ? (
                  <div className="flex items-center gap-1 overflow-hidden">
                    <div className="text-sm text-gray-600 truncate flex-1">{activeLine.itemDescription}</div>
                    {isDescriptionTruncated && (
                      <button
                        onClick={() => setShowFullDescription(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium flex-shrink-0 min-h-[44px] px-2"
                      >
                        ...
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">
                    {activeLine.itemDescription}
                    <button
                      onClick={() => setShowFullDescription(false)}
                      className="ml-2 text-blue-600 hover:text-blue-700 inline-flex items-center min-h-[44px] px-2"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
              {/* Extra info fields from OrderLine (configured via UserProfile) */}
              {(activeLine.extraInfoField2Value || activeLine.extraInfoField3Value) && (
                <div className="flex items-center gap-3 mt-1">
                  {activeLine.extraInfoField2Value && (
                    <span className="text-sm font-semibold text-red-600">{activeLine.extraInfoField2Value}</span>
                  )}
                  {activeLine.extraInfoField3Value && (
                    <span className="text-sm font-semibold text-green-600">{activeLine.extraInfoField3Value}</span>
                  )}
                </div>
              )}
            </div>

            {/* Location */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={18} className="text-blue-600" />
                <span className="text-sm font-medium text-gray-600">{t('warehouse.modules.picking.labels.location')}</span>
              </div>
              {editingLocation === activeLine.pListLineNo ? (
                <input
                  type="text"
                  value={newLocation}
                  onChange={(e) => onLocationChange?.(e.target.value)}
                  onBlur={() => onLocationEdit?.(activeLine.pListLineNo, newLocation)}
                  onKeyPress={(e) => handleKeyPress(e, 'location')}
                  className="text-xl font-bold w-full px-2 py-2 border-2 border-blue-400 rounded min-h-[44px]"
                  autoFocus
                />
              ) : (
                <div
                  className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 rounded px-2 py-2 -ml-2 min-h-[44px]"
                  onClick={() => onStartLocationEdit?.(activeLine.pListLineNo, activeLine.location)}
                >
                  <div className="text-xl font-bold text-gray-900">{activeLine.location}</div>
                  <Edit3 size={16} className="text-gray-400" />
                </div>
              )}
            </div>

            {/* Quantity */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle size={18} className="text-blue-600" />
                <span className="text-sm font-medium text-gray-600">{t('warehouse.modules.picking.labels.quantity')}</span>
              </div>
              {activeLine.itemType === 6 ? (
                /* ItemType=6: Serial-tracked - quantity only changes via serial pick modal */
                <div className="flex items-center gap-2 px-2 py-2 -ml-2 min-h-[44px]">
                  <div className="text-xl font-bold text-gray-900">
                    {activeLine.qtyPicked} av {activeLine.qty}
                  </div>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">SN</span>
                </div>
              ) : editingQuantity === activeLine.pListLineNo ? (
                <input
                  type="number"
                  value={newQuantity}
                  onChange={(e) => onQuantityChange?.(e.target.value)}
                  onBlur={() => onQuantityEdit?.(activeLine.pListLineNo, parseInt(newQuantity) || 0)}
                  onKeyPress={(e) => handleKeyPress(e, 'quantity')}
                  className="text-xl font-bold w-full px-2 py-2 border-2 border-blue-400 rounded min-h-[44px]"
                  autoFocus
                />
              ) : (
                <div
                  className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 rounded px-2 py-2 -ml-2 min-h-[44px]"
                  onClick={() => onStartQuantityEdit?.(activeLine.pListLineNo, activeLine.qtyPicked.toString())}
                >
                  <div className="text-xl font-bold text-gray-900">
                    {activeLine.qtyPicked} av {activeLine.qty}
                  </div>
                  <Edit3 size={16} className="text-gray-400" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
